import { RoutesManager } from "../shared/models/routes.interfaces";
import { RoleEnum } from "../core/models/permission-role.enum";
import { MenuItemId } from "../core/app-sidenav-content/menu-item.enum";

export class RolePermissionsHelper {

    static getPerrmitedRoleByRoute(route: string[]): RoleEnum[] {
        switch (route[0]) {
            // Landing page + billing stay open to DsrAgent; neither is a sidenav item.
            case RoutesManager.homePage:
            case RoutesManager.plans:
                return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.DsrManager, RoleEnum.DsrAgent, RoleEnum.InventoryManager];

            // Neither DSR role reaches the inventory or the DPO advisor.
            case RoutesManager.dpoAdvisor:
            case RoutesManager.systems:
                return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.InventoryManager];

            case RoutesManager.dashboard:
                return this.getDashboardRoles(route[1]);
                
            // My tasks is available to every role.
            case RoutesManager.tasks:
            case RoutesManager.general:
                return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.DsrManager, RoleEnum.DsrAgent, RoleEnum.InventoryManager, RoleEnum.Reviewer, RoleEnum.CmpAdmin];
                
            case RoutesManager.companySettings: 
                return this.getSettingsRoles(route[1]);

            // The feature page both DSR roles own.
            case RoutesManager.requests:
                return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.DsrManager, RoleEnum.DsrAgent];

            // DsrManager and CmpAdmin also get Reporting; its cards self-gate per dashboard via
            // REPORTING_DASHBOARDS.permissionRoute, so each role resolves only its own (DSR +
            // DSR audit log / Consent). Needs an explicit case: `reporting` is an ungrouped leaf
            // with no section to inherit from, and the default grants neither role.
            case RoutesManager.reporting:
                return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.DsrManager, RoleEnum.InventoryManager, RoleEnum.CmpAdmin];

            case RoutesManager.unusedAssets:
            case RoutesManager.userSearch:
            case RoutesManager.dashboardRequestHandling:
                return [RoleEnum.Admin, RoleEnum.Agent];

            case RoutesManager.policies:
            case RoutesManager.privacyCenter:
            case RoutesManager.dsr_setup:
            case RoutesManager.ai_agents:
                return [RoleEnum.Admin, RoleEnum.Agent];

            case RoutesManager.radar:
            case RoutesManager.discovery:
            case RoutesManager.employees:
            case RoutesManager.dataClassifier:
            case RoutesManager.processingActivities:
            case RoutesManager.vendorManagement:
            case RoutesManager.impactAssessment:
            case RoutesManager.dashboardDataClassification:
            case RoutesManager.dashboardDataInventory:
            case RoutesManager.ai_assets:
            case RoutesManager.risk_registry:
            case RoutesManager.risk_overview:
            case RoutesManager.compliance_overview:
            case RoutesManager.assessments:
            case RoutesManager.ai_assessments:
            case RoutesManager.compliance_assessments:
            case RoutesManager.ropa_assessments:
            case RoutesManager.vendor_assessments:
                return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.InventoryManager];

            case RoutesManager.dsr_overview:
            case RoutesManager.dsr_audit_log_overview:
            case RoutesManager.emailForwarding:
                return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.DsrManager];

            // Reporting-only "DSR California DROP" dashboard. Own case (not folded into the DSR
            // routes above) so its roles can change independently of the other DSR cards.
            case RoutesManager.dsr_california_drop_overview:
                return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.DsrManager];

            // cmp routes
            case RoutesManager.cmp:
            case RoutesManager.cmpWebsites:
            case RoutesManager.cmpOverview:
            case RoutesManager.consentManagement:
                return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.CmpAdmin];
            
            // Deny-by-default for the scoped roles (all keep profile/notifications):
            //   Reviewer   -> My tasks
            //   DsrAgent   -> Data subject requests + My tasks
            //   DsrManager -> Data subject requests + Reporting + My tasks
            // A route with no explicit case must NOT grant them. Listing them here silently
            // exposed every new route to all three (risks / ai-posture / reporting / developers).
            default:
                return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.InventoryManager];
        }
    }

    private static getDashboardRoles(route: RoutesManager): RoleEnum[] {
        switch (route) {
            case RoutesManager.dashboardRequestHandling:
                return [RoleEnum.Admin, RoleEnum.Agent];
            case RoutesManager.dashboardDataInventory:
            case RoutesManager.dashboardDataClassification:
                return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.InventoryManager];
            // No DSR role: the legacy dashboard is not in either DSR remit — DsrManager's
            // reporting surface is the Reporting page, whose DSR cards it still resolves.
            default:
                return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.InventoryManager];
        }
    }

    private static getSettingsRoles(route: RoutesManager): RoleEnum[] {
        switch (route) {
            case RoutesManager.profile:
                return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.DsrManager, RoleEnum.DsrAgent, RoleEnum.InventoryManager, RoleEnum.Reviewer, RoleEnum.CmpAdmin];
            case RoutesManager.notifications:
                return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.InventoryManager, RoleEnum.Reviewer];
            case RoutesManager.branding:
                return [RoleEnum.Admin, RoleEnum.Agent];

            default:
                return [RoleEnum.Admin, RoleEnum.Agent];
        }
    }

    static getPerrmitedRolesByMenuId(id: MenuItemId): RoleEnum[] {
        switch (id) {
            case MenuItemId.ConsentManagement:
            case MenuItemId.ConsentManagementOverview:
            case MenuItemId.ConsentManagementWebsites:
                return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.CmpAdmin];

            case MenuItemId.AiAgents:
                return [RoleEnum.Admin, RoleEnum.Agent];

            case MenuItemId.DataInventory:
            case MenuItemId.DataSources:
            case MenuItemId.Compliance:
            case MenuItemId.Discovery:
            case MenuItemId.Risk:
            case MenuItemId.VendorManagement:
            case MenuItemId.AiGovernance:
                return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.InventoryManager];

            case MenuItemId.WhatsNew:
                return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.DsrManager, RoleEnum.DsrAgent, RoleEnum.InventoryManager, RoleEnum.CmpAdmin];

            case MenuItemId.DsrHandling:
                return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.DsrManager, RoleEnum.DsrAgent];

            // No DsrManager: the legacy dashboard is out of the DSR remit (Reporting replaces it).
            case MenuItemId.Dashboard:
                return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.InventoryManager];

            // Deny-by-default for Reviewer — see getPerrmitedRoleByRoute. Also stops an
            // otherwise-empty section header rendering in the sidenav for that role.
            default:
                return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.DsrManager, RoleEnum.DsrAgent, RoleEnum.InventoryManager];
        }
    }

    // There are API calls that they are cross reference data, not a single route feature.
    
    // custom fields info, not the custom fields route.
    // Reviewer excluded: the portal `v2/custom-fields` endpoint forbids that role (403).
    static getPerrmitedRoleForCustomFields(): RoleEnum[] {
        return [
            RoleEnum.Admin, RoleEnum.Agent, RoleEnum.InventoryManager
        ];
    }

    // notifications info, not the notifications route in company settings
    static getPerrmitedRoleForNotifications(): RoleEnum[] {
        return [
            RoleEnum.Admin, RoleEnum.Agent, RoleEnum.InventoryManager,
        ];
    }

    // DSR brand phone verification settings (dsr/v1/brand/{id}/phone-verification), not by route
    static getPerrmitedRoleForPhoneVerification(): RoleEnum[] {
        return [
            RoleEnum.Admin,
        ];
    }

    // AI posture MDM package download (POST aispm/v1/mdm-package), not by route
    static getPerrmitedRoleForMdmPackage(): RoleEnum[] {
        return [
            RoleEnum.Admin,
        ];
    }

    // Mira AI agents (homepage panel + agent-availability fetch), not by route
    static getPerrmitedRoleForAiAgents(): RoleEnum[] {
        return [
            RoleEnum.Admin, RoleEnum.Agent,
        ];
    }

    // AI Template Generator run (POST assessment/v4/template-page/ai/runs), not by route.
    // Requires `template-page-v4.set` — the same permission as editing/saving an assessment template.
    // Reviewer excluded (read-only on assessments).
    static getPerrmitedRoleForAiTemplateRun(): RoleEnum[] {
        return [
            RoleEnum.Admin, RoleEnum.Agent, RoleEnum.InventoryManager,
        ];
    }

    // DSR related, not by route
    static getPerrmitedRoleForPrivacyRights(): RoleEnum[] {
        return [
            RoleEnum.Admin, RoleEnum.Agent, RoleEnum.DsrManager, RoleEnum.DsrAgent
        ];
    }

    static getPerrmitedRoleForGroups(): RoleEnum[] {
        return [
            RoleEnum.Admin, RoleEnum.Agent, RoleEnum.DsrManager, RoleEnum.DsrAgent
        ];
    }

    // brands info, not the brands page
    static getPerrmitedRoleForBrands(): RoleEnum[] {
        return [
            RoleEnum.Admin, RoleEnum.Agent, RoleEnum.DsrAgent, RoleEnum.DsrManager
        ];
    }

    // templates info, not the templates page
    static getPerrmitedRoleForDsrTemplates(): RoleEnum[] {
        return [
            RoleEnum.Admin, RoleEnum.Agent, RoleEnum.DsrAgent, RoleEnum.DsrManager
        ];
    }

}